import { DataItem } from 'scrivito'

import { FilterValues, WeekRange } from '../../types'
import { buildQueryFilters } from '../../utils/dataTransformations'
import { TimerecordTeam } from '@/Data/TimerecordTeam/TimerecordTeamDataClass'
import { TimerecordUser } from '@/Data/TimerecordUser/TimerecordUserDataClass'
import { ensureString } from '@/utils/ensureString'
import { getTimerecordDurations } from '@/utils/timerecordUtils'

/**
 * All Times shows everyone's records, regardless of the current user's role.
 * Scoping comes only from the filters:
 * - a selected person → that person's records (API `user` filter),
 * - a selected leader → that leader's team (filtered in memory),
 * - nothing / `team: 'all'` → everyone.
 */
export const selectedPersonId = (
  appliedFilters: FilterValues,
): string | null => {
  const { team, isTeamLeader } = appliedFilters
  if (!team || team === 'all' || isTeamLeader) return null
  return team
}

export const selectedLeaderId = (
  appliedFilters: FilterValues,
): string | null => {
  const { team, isTeamLeader } = appliedFilters
  if (!team || team === 'all' || !isTeamLeader) return null
  return team
}

export const buildAllTimesFilters = (
  appliedFilters: FilterValues,
  weekRange: WeekRange,
): ReturnType<typeof buildQueryFilters> => {
  const personId = selectedPersonId(appliedFilters)
  return {
    ...buildQueryFilters({
      userId: '',
      appliedFilters,
      weekRange,
      showAll: true,
    }),
    ...(personId ? { user: personId } : {}),
  }
}

/**
 * Records with in-memory filtering (leader team, unbilled) or an order
 * filter load the full range so pages and totals stay complete.
 */
export const shouldPaginate = (appliedFilters: FilterValues): boolean =>
  !selectedPersonId(appliedFilters) &&
  !selectedLeaderId(appliedFilters) &&
  !appliedFilters.order &&
  !appliedFilters.unbilled

const isAccountDirector = (userId: string): boolean =>
  !!TimerecordUser.get(userId)?.get('accountDirector')

const isTeamLeader = (userId: string): boolean =>
  TimerecordTeam.all()
    .transform({ filters: { leader: userId } })
    .take().length > 0

export const canManageTeamRecords = (userId: string): boolean =>
  !!userId && (isAccountDirector(userId) || isTeamLeader(userId))

/**
 * Everyone can see all records, but only the owner or a manager (account
 * director / team leader) may edit, copy, delete or change their status.
 */
export const canEditRecord = (
  record: DataItem,
  currentUserId: string,
  canManage: boolean,
): boolean =>
  !!currentUserId &&
  (canManage || ensureString(record.get('user')) === currentUserId)

const getTeamMemberIds = (leaderId: string): string[] => [
  ...new Set(
    TimerecordTeam.all()
      .transform({ filters: { leader: leaderId } })
      .take()
      .map((item) => ensureString(item.get('member'))),
  ),
]

/**
 * Applies the filtering the API query cannot express: the selected leader's
 * team membership and the `unbilled` filter.
 */
export const filterRecordsForView = (
  records: DataItem[],
  appliedFilters: FilterValues,
): DataItem[] => {
  const leaderId = selectedLeaderId(appliedFilters)
  const teamMemberIds = leaderId ? getTeamMemberIds(leaderId) : null

  return records
    .filter(
      (record) =>
        !teamMemberIds ||
        teamMemberIds.includes(ensureString(record.get('user'))),
    )
    .filter(
      (record) =>
        !appliedFilters.unbilled ||
        getTimerecordDurations(record).nonBillableDuration > 0,
    )
}
